import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import {Injectable} from '@angular/core';
import {JwtHelperService} from '@auth0/angular-jwt';
import {AuthService} from '../services/auth.service';
import {SharedService} from '../services/sharedService';
import {Observable, from, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})

export class AuthGuard  {
    constructor(private router: Router, public jwtHelper: JwtHelperService,
                private auth: AuthService, public authService: AuthService, public sharedService: SharedService) {
    }

    // Every branch of this guard used to `return of(true)`, including the
    // no-token branch, so it never actually denied anything: it relied on the
    // side effect of router.navigate to redirect. The guarded route was still
    // activated, so its component constructed and fired its ngOnInit data calls
    // unauthenticated before being torn down, producing 401s and error toasts.
    // The expiry branch additionally had the same discarded-return bug as
    // ControlModuleGuard: the values returned inside the promise callback
    // returned from that callback, not from canActivate.
    //
    // Redirecting and returning false is the correct pair: navigate() starts the
    // replacement navigation, false cancels the current one.
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        if (route.queryParams.nonce) {
            this.router.navigate(['login'],
                {queryParams: {nonce: route.queryParams.nonce, returnUrl: route.queryParams.returnUrl}});
            return of(false);
        }

        const token = localStorage.getItem('access_token');
        if (!token || token === 'undefined') {
            this.router.navigate(['login'], {queryParams: {url: route.url}});
            return of(false);
        }

        if (this.jwtHelper.isTokenExpired()) {
            return this.renewExpiredToken(token);
        }

        return this.auth.getCurrentUser().pipe(
            map(user => {
                this.auth.setLoginStatus(true);
                this.auth.setUserEnabled(!!user && user['status'] === 'ENABLED');
                return true;
            }),
            // A failure to read the current user must not block navigation: the
            // token is still valid and the server remains the real authority.
            // This preserves the previous fire-and-forget behaviour of the
            // getCurrentUser subscription.
            catchError(() => of(true))
        );
    }

    // On a successful renewal the page is reloaded so every service re-reads the
    // new token, and the current navigation is cancelled because the document is
    // being replaced anyway. On failure the session is cleared and the user is
    // sent to login.
    private renewExpiredToken(token: string): Observable<boolean> {
        return from(this.authService.requestToken(token)).pipe(
            switchMap(renewed => {
                if (renewed === undefined || renewed === null) {
                    return this.failToLogin();
                }
                localStorage.setItem('access_token', renewed);
                this.auth.setLoginStatus(true);
                location.reload();
                return of(false);
            }),
            catchError(() => this.failToLogin())
        );
    }

    private failToLogin(): Observable<boolean> {
        return this.authService.logout().pipe(
            catchError(() => of(null)),
            map(() => {
                localStorage.removeItem('access_token');
                this.router.navigate(['login']);
                return false;
            })
        );
    }
}
